"use client";

import { useEffect, useRef } from "react";
import { firePageLoad, fireProductImpression } from "../../services/analytics";
import {
  CHANNEL_LINEUP_PAGE_LOAD,
  type AnalyticsIdentified,
  productImpressionItems
} from "./lineup-analytics.shared";
import { useLineupViewState } from "./lineup-view.client";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * AN1's page load and AN2's product impression — the two reports a view makes about itself, as
 * opposed to the reports a control makes about a click. The client-boundary argument.
 */

export interface LineupAnalyticsProps {
  /**
   * The Signature grid's columns, in the order they are drawn — AN2 numbers from this array after
   * dropping the entries with no analytics id.
   */
  signaturePackages?: readonly AnalyticsIdentified[];
  /** The Genre Packs view's packs, same contract. `LineupPack[]` satisfies it. */
  genrePacks?: readonly AnalyticsIdentified[];
  /**
   * Which view value means Genre Packs. Defaults to the organism's own `ChannelLineupView`
   * vocabulary, which is what `LineupSelectedView` switches on (`lineup-view.client.tsx`) — pass it
   * only if a blok authors something else.
   */
  genreViewValue?: string;
}

const DEFAULT_GENRE_VIEW_VALUE = "genre";

function LineupAnalyticsImpl({
  signaturePackages,
  genrePacks,
  genreViewValue = DEFAULT_GENRE_VIEW_VALUE
}: LineupAnalyticsProps) {
  const { view } = useLineupViewState();

  /* Held in a ref, and the effect depends on the view alone. */
  const packages = useRef({
    signaturePackages,
    genrePacks
  });
  packages.current = {
    signaturePackages,
    genrePacks
  };
  /* The page's own load event already fires once per page (the v3 bootstrap),
     so AN1 here is for a switch back to the grid, never for the mount. */
  const shownBefore = useRef(false);

  useEffect(() => {
    const isGenre = view === genreViewValue;
    const shown = isGenre ?
      packages.current.genrePacks :
      packages.current.signaturePackages;

    /* AN2 — one impression per view shown, for THAT view's packages. An empty
       list pushes nothing; `fireProductImpression` owns that gate. */
    fireProductImpression({ items: productImpressionItems(shown) });

    /* AN1 — the Signature grid only. Audit question 11 ("should a visitor who
       lands on Genre Packs also send one?") is raised, not decided here. */
    if (!isGenre && shownBefore.current) {
      firePageLoad(CHANNEL_LINEUP_PAGE_LOAD);
    }
    shownBefore.current = true;
  }, [genreViewValue, view]);

  return null;
}
export const LineupAnalytics = withSafeRender(LineupAnalyticsImpl, "LineupAnalytics");

export default LineupAnalytics;
